import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, X } from "lucide-react";
import { LANGUAGES } from '../../../utils/AppConstants';
import { useAppContext } from '../../../context/AppContext';
import './LanguageModal.css';
import bhashiniLogo from "../../../assets/images/bhashini-logo.png";
import PropTypes from "prop-types";
import useLocalization from "../../../hooks/useLocalization";


export default function LanguageModal({ isOpen, onClose }) {
  const [search, setSearch] = useState("");
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const { selectedLanguageData, setSelectedLanguageData } = useAppContext();
  const { t } = useLocalization();

  const filteredLanguages = useMemo(() => {
    return LANGUAGES.filter(
      (lang) => lang.isActive &&
        (lang.label.toLowerCase().includes(search.toLowerCase()) ||
          lang.code.toLowerCase().includes(search.toLowerCase()))
    );
  }, [search]);

  const [sliceCount, setSliceCount] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 350) {
        setSliceCount(2);
      } else if (window.innerWidth <= 518) {
        setSliceCount(2);
      }
      else if (window.innerWidth <= 768) {
        setSliceCount(3);
      } else {
        setSliceCount(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const topLanguages = useMemo(() => {
    return LANGUAGES.filter(
      lang => lang.isActive && lang.code !== selectedLanguageData?.code && ['en', 'hi', 'pa', 'te', 'ta'].includes(lang.code)
    ).slice(0, sliceCount);
  }, [selectedLanguageData, sliceCount]);


  const handleLanguageSelect = (lang) => {
    setSelectedLanguageData(lang);
    setLanguageModalOpen(false);
    onClose();
  };

  const handleCloseModal = () => {
    setLanguageModalOpen(false);
    setSearch("");
  };

  const handleOpenModal = () => {
    setLanguageModalOpen(true);
    setSearch("");
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="language-chips-container mx-3">
        {
          selectedLanguageData && selectedLanguageData?.code !== '' && selectedLanguageData?.label !== '' && (
            <span className="selected-language-chip">
              {selectedLanguageData?.label}
            </span>
          )
        }

        {topLanguages.map((lang) => (
          <span
            key={lang.code}
            className="language-chip"
            onClick={() => handleLanguageSelect(lang)}
          >
            {lang.label}
          </span>
        ))}

        <button
          onClick={handleOpenModal}
          className="more-languages-btn"
          aria-label="More languages"
        >
          <MoreHorizontal size={20} />
        </button>

        <button
          onClick={onClose}
          className="close-chips-btn"
          aria-label="Close language selector"
        >
          <X size={18} />
        </button>
      </div>

      {languageModalOpen && createPortal(
        <div className="language-modal-overlay" onClick={handleCloseModal}>
          <div
            className="language-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="language-modal-header">
              <h2>{t("language.selectLanguage")}</h2>
              <button
                onClick={handleCloseModal}
                className="close-modal-btn"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="language-search-container">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="language-search-input"
                autoFocus
              />
            </div>

            <div className="language-list-container">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    className="language-list-item"
                    onClick={() => handleLanguageSelect(lang)}
                  >
                    {lang.label}
                  </button>
                ))
              ) : null}
            </div>

            <div className="p-1 border border-t-[#ddd] flex items-end justify-center text-sm gap-1">
              <span>{t("navbar.poweredBy")}</span>  <img src={bhashiniLogo} alt="bhashiniLogo" />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}


LanguageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
